import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { MaskLine, Rise } from "@/components/motion/Reveal";
import { INSIGHTS } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldArticle, ldBreadcrumb } from "@/seo/seoConfig";

export default function InsightArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const article = INSIGHTS.find((a) => a.slug === slug);
  const idx = INSIGHTS.findIndex((a) => a.slug === slug);
  const next = INSIGHTS[(idx + 1) % INSIGHTS.length];

  useEffect(() => {
    if (!article) navigate("/insights", { replace: true });
  }, [article, navigate]);

  if (!article) return null;

  return (
    <div className="pt-40">
      <Seo
        title={article.title}
        description={article.dek}
        path={`/insights/${article.slug}`}
        type="article"
        keywords={[article.cat, `${article.cat} lawyer`, `${article.cat} UAE`, `${article.cat} India`, article.author, "RPLC UAE"]}
        jsonLd={[
          ldArticle(article, `/insights/${article.slug}`),
          ldBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: article.title, path: `/insights/${article.slug}` },
          ]),
        ]}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise>
          <Link to="/insights" className="chapter-num inline-flex items-center gap-2 text-[#A19D94] hover:text-[#F4F1EB] transition-colors">
            <ArrowLeft size={14} strokeWidth={1.25} /> {t.insights.back}
          </Link>
        </Rise>

        <div className="mt-12 flex flex-wrap items-center gap-4 chapter-num">
          <span className="text-[#C84C35]">{article.cat}</span>
          <span className="text-[#66635C]">·</span>
          <span>{article.read}</span>
          <span className="text-[#66635C]">·</span>
          <span>{article.date}</span>
        </div>

        <h1 className="font-serif tracking-tight leading-[1] text-[9vw] lg:text-[6vw] mt-8 max-w-[95%]">
          {article.title.split(" ").map((w, i) => (
            <MaskLine key={i} delay={0.1 + i * 0.02}>{w} </MaskLine>
          ))}
        </h1>

        <Rise delay={0.4}>
          <p className="mt-12 font-serif text-2xl lg:text-3xl italic text-[#A19D94] max-w-4xl leading-tight">{article.dek}</p>
        </Rise>

        <Rise delay={0.5}>
          <div className="mt-16 flex items-center gap-4 pb-8 border-b border-[#141414]">
            <div className="w-12 h-12 border border-[#222] flex items-center justify-center font-serif text-lg">
              {article.author.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div>
              <div className="text-[#F4F1EB]">{article.author}</div>
              <div className="chapter-num text-[#66635C]">Partner · RPLC UAE</div>
            </div>
          </div>
        </Rise>
      </section>

      <article className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <div className="chapter-num text-[#66635C] mb-4">Article</div>
              <div className="font-serif text-3xl italic-accent text-[#F4F1EB]">
                {String(idx + 1).padStart(2, "0")} / {String(INSIGHTS.length).padStart(2, "0")}
              </div>
              <div className="hairline my-8" />
              <div className="chapter-num text-[#66635C] mb-3">In this piece</div>
              <ol className="space-y-2 text-sm text-[#A19D94]">
                {article.body.slice(0, 4).map((p, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="chapter-num text-[#66635C]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="line-clamp-2">{p.split(".")[0]}.</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-8">
            {article.body.map((p, i) => (
              <Rise key={i} delay={i * 0.04}>
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-1 lg:col-span-1 pt-2">
                    <span className="chapter-num text-[#C84C35]">§{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <p className="col-span-11 font-serif text-xl lg:text-2xl leading-[1.55] text-[#F4F1EB] font-light">
                    {i === 0 ? (
                      <>
                        <span className="font-serif text-6xl lg:text-7xl float-left leading-[0.85] mr-3 mt-1 text-[#C84C35] italic-accent">{p[0]}</span>
                        {p.slice(1)}
                      </>
                    ) : (
                      p
                    )}
                  </p>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </article>

      {/* NEXT */}
      <section className="mt-32 border-t border-[#141414]">
        <Link to={`/insights/${next.slug}`} className="block group py-24">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="chapter-num text-[#66635C]">Next in insights</div>
            <div className="mt-6 flex items-start justify-between gap-8">
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1] tracking-tight max-w-4xl group-hover:text-[#C84C35] transition-colors duration-700">
                {next.title}
              </h2>
              <div className="text-[#66635C] group-hover:text-[#C84C35] transition-colors duration-700 pt-2 shrink-0">
                <ArrowUpRight size={40} strokeWidth={1} />
              </div>
            </div>
          </div>
        </Link>
      </section>
    </div>
  );
}
